/**
 * MealPhotoAnalyzer.jsx
 * AI-powered meal photo analysis with multiple photo angles
 * Matches Customer Portal multi-photo capture functionality
 */
import React, { useState, useRef } from 'react';
import { X, Camera, Upload, RefreshCw, Plus, Sparkles, AlertCircle, Check, Eye, SidebarClose, Square, ImageIcon } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import CameraCapture from '@/components/nutrition/CameraCapture';
import { compressImage } from '@/utils/imageCompressor';
import { useTranslation } from 'react-i18next';
import { isNative, takePhoto } from '@/utils/capacitor';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Photo types for multi-angle capture
const PHOTO_TYPES = [
  { 
    key: 'top', 
    label: 'Draufsicht', 
    required: true,
    icon: '⬆️',
    description: 'Halte das Handy direkt über den Teller'
  },
  { 
    key: 'side', 
    label: 'Seitenansicht', 
    required: true,
    icon: '➡️',
    description: 'Auf Augenhöhe mit dem Teller fotografieren'
  },
  { 
    key: 'detail', 
    label: 'Detail',
    required: false,
    icon: '🔲',
    description: 'Zeige versteckte Zutaten oder Saucen'
  },
  { 
    key: 'extra', 
    label: 'Weiteres Foto',
    required: false,
    icon: '➕',
    description: 'Mehr Fotos = genauere Analyse'
  }
];

export const MealPhotoAnalyzer = ({ isOpen, onClose, onMealAdded, portalToken, customerId, mealType }) => {
  // Multi-photo state
  const [photos, setPhotos] = useState({
    top: null,
    side: null,
    detail: null,
    extra: null
  });
  const [previews, setPreviews] = useState({
    top: null,
    side: null,
    detail: null,
    extra: null
  });
  const [activePhotoType, setActivePhotoType] = useState(null);
  
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  const [quickDetect, setQuickDetect] = useState(null);
  const [saving, setSaving] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraTargetKey, setCameraTargetKey] = useState(null);
  const fileInputRef = useRef(null);

  const getDefaultConsumedTimeForMeal = (mealTypeValue) => {
    const mapping = {
      breakfast: '08:00',
      snack_morning: '10:00',
      lunch: '12:30',
      snack_afternoon: '16:00',
      dinner: '19:00',
      snack_evening: '21:00'
    };
    return mapping[mealTypeValue] || '12:30';
  };

  // Count photos
  const photoCount = Object.values(photos).filter(Boolean).length;
  const requiredCount = PHOTO_TYPES.filter(t => t.required && photos[t.key]).length;
  const requiredTotal = PHOTO_TYPES.filter(t => t.required).length;

  // Handle photo selection for a specific type
  const handlePhotoSelect = (type) => {
    setActivePhotoType(type);
    fileInputRef.current?.click();
  };

  // Handle image selection
  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file || !activePhotoType) return;
    
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Bild zu gross (max. 10MB)');
      return;
    }
    
    setPhotos(prev => ({ ...prev, [activePhotoType]: file }));
    setResult(null);
    
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviews(prev => ({ ...prev, [activePhotoType]: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  // Remove a photo
  const removePhoto = (type) => {
    setPhotos(prev => ({ ...prev, [type]: null }));
    setPreviews(prev => ({ ...prev, [type]: null }));
  };

  // Analyze the photos - 2-Stage approach
  const handleAnalyze = async () => {
    // Need at least one photo
    const hasPhotos = Object.values(photos).some(Boolean);
    if (!hasPhotos || !portalToken) {
      toast.error('Bitte mindestens ein Foto aufnehmen');
      return;
    }
    
    setAnalyzing(true);
    setResult(null);
    setQuickDetect(null);
    
    try {
      // Use the first available photo for analysis
      const primaryPhoto = photos.top || photos.side || photos.detail || photos.extra;
      
      // Compress image before sending
      const compressedBase64 = await compressImage(primaryPhoto);

      // Stage 1: Quick Detection (~2-3s)
      try {
        const quickRes = await axios.post(
          `${API}/meal-assistant/quick-detect-food`,
          {
            customer_id: customerId,
            image_base64: compressedBase64,
            meal_type: mealType || 'lunch'
          },
          { 
            headers: { Authorization: `Bearer ${portalToken}` },
            timeout: 15000
          }
        );
        setQuickDetect(quickRes.data);
      } catch (err) {
        console.warn('Quick detect failed:', err.message);
      }
      
      // Stage 2: Full Analysis
      try {
        const response = await axios.post(
          `${API}/meal-assistant/analyze-food-photo`,
          {
            customer_id: customerId,
            image_base64: compressedBase64,
            meal_type: mealType || 'lunch'
          },
          { 
            headers: { Authorization: `Bearer ${portalToken}` },
            timeout: 45000
          }
        );
        
        setResult(response.data);
        setQuickDetect(null);
        toast.success('Analyse abgeschlossen!');
      } catch (err) {
        console.error('Analysis error:', err);
        if (err.code === 'ECONNABORTED') {
          toast.error('Analyse hat zu lange gedauert. Bitte versuche es erneut.');
        } else {
          const errorMsg = err.response?.data?.detail || 'Analyse fehlgeschlagen';
          toast.error(errorMsg);
        }
      }
      
      setAnalyzing(false);
    } catch (err) {
      console.error('Error:', err);
      toast.error('Fehler beim Verarbeiten des Bildes');
      setAnalyzing(false);
    }
  };

  // Save meal to nutrition tracking
  const handleSaveMeal = async () => {
    if (!result || !portalToken) return;
    
    const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
    const consumedAt = getDefaultConsumedTimeForMeal(mealType || 'lunch');
    
    setSaving(true);
    try {
      // If demo mode, just create entry directly
      if (result.is_demo) {
        await axios.post(
          `${API}/portal/nutrition/entries`,
          {
            date: today,
            meal_type: mealType || 'lunch',
            consumed_at: consumedAt,
            food_name: result.meal_name,
            serving_size: 100,
            calories: result.total_calories,
            protein: result.total_protein,
            carbs: result.total_carbs,
            fat: result.total_fat,
            notes: 'Via KI-Foto-Analyse (Demo)'
          },
          { headers: { Authorization: `Bearer ${portalToken}` } }
        );
      } else {
        // Use the dedicated endpoint if available
        try {
          await axios.post(
            `${API}/meal-assistant/photo-to-entry/${customerId}`,
            {
              meal_name: result.meal_name,
              meal_type: mealType || 'lunch',
              consumed_at: consumedAt,
              total_calories: result.total_calories,
              total_protein: result.total_protein,
              total_carbs: result.total_carbs,
              total_fat: result.total_fat,
              components: result.components
            },
            { headers: { Authorization: `Bearer ${portalToken}` } }
          );
        } catch {
          // Fallback to direct entry
          await axios.post(
            `${API}/portal/nutrition/entries`,
            {
              date: today,
              meal_type: mealType || 'lunch',
              consumed_at: consumedAt,
              food_name: result.meal_name,
              serving_size: 100,
              calories: result.total_calories,
              protein: result.total_protein,
              carbs: result.total_carbs,
              fat: result.total_fat,
              notes: 'Via KI-Foto-Analyse'
            },
            { headers: { Authorization: `Bearer ${portalToken}` } }
          );
        }
      }
      
      toast.success('Mahlzeit gespeichert!');
      onMealAdded?.(result);
      handleReset();
      onClose();
    } catch (err) {
      console.error('Save error:', err);
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  // Reset state
  const handleReset = () => {
    setPhotos({ top: null, side: null, detail: null, extra: null });
    setPreviews({ top: null, side: null, detail: null, extra: null });
    setActivePhotoType(null);
    setResult(null);
  };

  if (!isOpen) return null;

  return (
    <div className="absolute inset-0 bg-black/95 z-50 flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-zinc-700">
        <div className="flex items-center gap-3">
          <button onClick={onClose} className="p-2 bg-zinc-800 rounded-lg">
            <X className="w-5 h-5 text-zinc-400" />
          </button>
          <div className="flex-1">
            <h2 className="text-white font-bold">Mahlzeit fotografieren</h2>
            <p className="text-zinc-500 text-xs">
              {requiredCount}/{requiredTotal} Pflichtfotos | {photoCount} Fotos total
            </p>
          </div>
          <Sparkles className="w-6 h-6 text-yellow-400" />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Info Banner */}
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-3 space-y-2">
          <p className="text-yellow-300 text-xs">
            💡 Mindestens 2 Fotos für genaue Analyse. Mehr Fotos = bessere Erkennung von versteckten Zutaten und Portionsgrössen.
          </p>
          <p className="text-yellow-200/80 text-xs font-medium">
            🍴 <strong>Tipp für präzise Portionsgrössen:</strong> Lege ein Referenzobjekt neben das Essen - Besteck (Gabel, Messer, Löffel) oder eine Kreditkarte.
          </p>
        </div>

        {/* Photo Capture Grid */}
        {!result && (
          <div className="grid grid-cols-2 gap-3">
            {PHOTO_TYPES.map((type) => (
              <div key={type.key} className="relative">
                {previews[type.key] ? (
                  <div className="relative">
                    <img 
                      src={previews[type.key]} 
                      alt={type.label} 
                      className="w-full h-28 object-cover rounded-xl"
                    />
                    <button
                      onClick={() => removePhoto(type.key)}
                      className="absolute top-1 right-1 p-1 bg-black/60 rounded-full"
                    >
                      <X className="w-3 h-3 text-white" />
                    </button>
                    <div className="absolute bottom-1 left-1 px-2 py-0.5 bg-green-500/80 rounded text-[10px] text-white">
                      ✓ {type.label}
                    </div>
                  </div>
                ) : (
                  <div
                    className={`w-full h-28 rounded-xl border-2 border-dashed flex flex-col items-center justify-center gap-1 transition-colors ${
                      type.required 
                        ? 'border-yellow-500/50 bg-yellow-500/5' 
                        : 'border-zinc-600 bg-zinc-800/30'
                    }`}
                  >
                    <span className="text-lg">{type.icon}</span>
                    <span className={`text-xs font-medium ${type.required ? 'text-yellow-400' : 'text-zinc-400'}`}>
                      {type.label}{type.required ? '*' : ''}
                    </span>
                    <div className="flex gap-2 mt-1">
                      <button
                        onClick={async () => {
                          if (isNative) {
                            const dataUrl = await takePhoto();
                            if (dataUrl) {
                              const res = await fetch(dataUrl);
                              const blob = await res.blob();
                              const file = new File([blob], `photo_${Date.now()}.jpg`, { type: 'image/jpeg' });
                              setPhotos(prev => ({ ...prev, [type.key]: file }));
                              setPreviews(prev => ({ ...prev, [type.key]: dataUrl }));
                            }
                          } else {
                            setCameraTargetKey(type.key);
                            setCameraOpen(true);
                          }
                        }}
                        className="px-2 py-1 text-[10px] bg-yellow-500/20 text-yellow-400 rounded-md border border-yellow-500/30"
                      >
                        Kamera
                      </button>
                      <button
                        onClick={() => handlePhotoSelect(type.key)}
                        className="px-2 py-1 text-[10px] bg-zinc-700 text-zinc-300 rounded-md border border-zinc-600"
                      >
                        Galerie
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept=".jpg,.jpeg,.png,.heic,.heif,.webp,.gif"
          onChange={handleImageSelect}
          className="hidden"
        />
        
        <CameraCapture
          open={cameraOpen}
          onOpenChange={setCameraOpen}
          onCapture={(file) => {
            if (!cameraTargetKey) return;
            const reader = new FileReader();
            reader.onloadend = () => {
              setPhotos(prev => ({ ...prev, [cameraTargetKey]: file }));
              setPreviews(prev => ({ ...prev, [cameraTargetKey]: reader.result }));
            };
            reader.readAsDataURL(file);
          }}
        />

        {/* Analyze Button */}
        {photoCount > 0 && !result && !analyzing && (
          <button
            onClick={handleAnalyze}
            className="w-full py-3 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-xl text-black font-bold flex items-center justify-center gap-2"
          >
            <Sparkles className="w-5 h-5" />
            Mit KI analysieren ({photoCount} Foto{photoCount > 1 ? 's' : ''})
          </button>
        )}

        {/* Analyzing State - 2-Stage Display */}
        {analyzing && (
          <div className="bg-zinc-800/70 rounded-xl p-6 text-center">
            {quickDetect?.food_names?.length > 0 ? (
              <>
                <Check className="w-8 h-8 text-green-400 mx-auto mb-2" />
                <p className="text-white font-medium mb-2">Erkannt:</p>
                <div className="flex flex-wrap gap-1.5 justify-center mb-4">
                  {quickDetect.food_names.map((name, i) => (
                    <span key={i} className="px-2.5 py-1 bg-yellow-500/15 border border-yellow-500/30 rounded-full text-yellow-300 text-xs">
                      {name}
                    </span>
                  ))}
                </div>
                <div className="flex items-center gap-2 justify-center">
                  <RefreshCw className="w-4 h-4 text-yellow-400 animate-spin" />
                  <p className="text-zinc-400 text-sm">Nährwerte werden berechnet...</p>
                </div>
              </>
            ) : (
              <>
                <RefreshCw className="w-10 h-10 text-yellow-400 animate-spin mx-auto mb-3" />
                <p className="text-white font-medium">Analysiere Mahlzeit...</p>
                <p className="text-zinc-500 text-sm mt-1">KI identifiziert Lebensmittel und berechnet Nährwerte</p>
              </>
            )}
          </div>
        )}

        {/* Analysis Result */}
        {result && (
          <div className="space-y-3">
            {/* Demo Warning */}
            {result.is_demo && (
              <div className="bg-yellow-500/20 border border-yellow-500/30 rounded-lg p-3 flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-yellow-400 flex-shrink-0" />
                <p className="text-yellow-400 text-sm">{result.notes}</p>
              </div>
            )}

            {/* Meal Name */}
            <div className="bg-zinc-800/70 rounded-xl p-4">
              <p className="text-yellow-400 text-xs font-medium mb-1">Erkannte Mahlzeit</p>
              <p className="text-white font-bold text-lg">{result.meal_name}</p>
            </div>

            {/* Total Nutrition */}
            <div className="bg-gradient-to-br from-yellow-500/20 to-orange-500/10 rounded-xl p-4 border border-yellow-500/30">
              <p className="text-yellow-400 text-xs font-medium mb-3">Gesamte Nährwerte</p>
              <div className="grid grid-cols-4 gap-2">
                <div className="text-center">
                  <p className="text-2xl font-bold text-white">{result.total_calories}</p>
                  <p className="text-zinc-500 text-xs">kcal</p>
                </div>
                <div className="text-center">
                  <p className="text-xl font-bold text-blue-400">{result.total_protein}g</p>
                  <p className="text-zinc-500 text-xs">Protein</p>
                </div>
                <div className="text-center">
                  <p className="text-xl font-bold text-orange-400">{result.total_carbs}g</p>
                  <p className="text-zinc-500 text-xs">Kohlenhy.</p>
                </div>
                <div className="text-center">
                  <p className="text-xl font-bold text-purple-400">{result.total_fat}g</p>
                  <p className="text-zinc-500 text-xs">Fett</p>
                </div>
              </div>
            </div>

            {/* Components */}
            {result.components && result.components.length > 0 && (
              <div className="bg-zinc-800/70 rounded-xl p-4">
                <p className="text-zinc-400 text-xs font-medium mb-3">Erkannte Komponenten</p>
                <div className="space-y-2">
                  {result.components.map((comp, i) => (
                    <div key={i} className="flex items-center justify-between py-2 border-b border-zinc-700/50 last:border-0">
                      <div>
                        <p className="text-white text-sm">{comp.name}</p>
                        <p className="text-zinc-500 text-xs">{comp.grams}g</p>
                      </div>
                      <p className="text-yellow-400 text-sm">{comp.calories} kcal</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Confidence */}
            {result.confidence && result.confidence !== 'demo' && (
              <div className="flex items-center gap-2 text-zinc-500 text-xs">
                <Check className="w-4 h-4 text-green-400" />
                Konfidenz: {result.confidence}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Save Button */}
      {result && (
        <div className="p-4 border-t border-zinc-700">
          <button
            onClick={handleSaveMeal}
            disabled={saving}
            className="w-full py-3 bg-yellow-500 rounded-xl text-black font-bold flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {saving ? (
              <RefreshCw className="w-5 h-5 animate-spin" />
            ) : (
              <>
                <Plus className="w-5 h-5" />
                Mahlzeit speichern
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default MealPhotoAnalyzer;
